import InfoIcon from '@mui/icons-material/Info';
import { Alert, AlertTitle, Box } from '@mui/material';
import React, { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { graphql, useFragment } from 'react-relay';

import {
  BOLETO_DELINQUENCY_TYPES_ENUM,
  BOLETO_DELINQUENCY_STATUS_ENUM,
} from '@woovi/shared';

import type { PaymentActiveBoletoDelinquencyWarning_charge$key } from './__generated__/PaymentActiveBoletoDelinquencyWarning_charge.graphql.ts';

type PaymentActiveBoletoDelinquencyWarningProps = {
  charge: PaymentActiveBoletoDelinquencyWarning_charge$key;
};

const PaymentActiveBoletoDelinquencyWarning = (
  props: PaymentActiveBoletoDelinquencyWarningProps,
): ReactNode => {
  const { t } = useTranslation();

  const charge = useFragment<PaymentActiveBoletoDelinquencyWarning_charge$key>(
    graphql`
      fragment PaymentActiveBoletoDelinquencyWarning_charge on Charge {
        paymentMethods {
          ... on PaymentMethodBoleto {
            delinquency {
              type
              status
            }
          }
        }
      }
    `,
    props.charge,
  );

  const boletoPaymentMethod = charge.paymentMethods?.find(
    (pm) => pm?.delinquency,
  );

  const delinquency = boletoPaymentMethod?.delinquency;

  const shouldShowWarning =
    delinquency?.status === BOLETO_DELINQUENCY_STATUS_ENUM.ACTIVE;

  const getWarningText = () => {
    if (!delinquency?.type) {return '';}

    switch (delinquency.type) {
      case BOLETO_DELINQUENCY_TYPES_ENUM.PROTEST:
        return t(
          'Stay alert: if this charge is not paid by the due date, it may be **protested** in a notary office.',
        );

      case BOLETO_DELINQUENCY_TYPES_ENUM.NEGATIVATION:
        return t(
          'Stay alert: if this charge is not paid by the due date, it may be **negatively reported** to credit protection agencies.',
        );

      default:
        return t(
          'Stay alert: if this charge is not paid by the due date, it may be **negatively reported** to credit protection agencies.',
        );
    }
  };

  const getWarningTitle = () => {
    if (!delinquency?.type) {return '';}

    switch (delinquency.type) {
      case BOLETO_DELINQUENCY_TYPES_ENUM.PROTEST:
        return t('Protest Warning');

      case BOLETO_DELINQUENCY_TYPES_ENUM.NEGATIVATION:
        return t('Negative Reporting Warning');

      default:
        return t('Important Warning');
    }
  };

  if (!shouldShowWarning) {
    return null;
  }

  return (
    <Alert
      severity='info'
      icon={<InfoIcon />}
      sx={{
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
        border: '1px solid #bbdefb',
        '& .MuiAlert-icon': {
          color: '#1976d2',
        },
      }}
    >
      <AlertTitle sx={{ color: '#1565c0', fontWeight: 'bold' }}>
        {getWarningTitle()}
      </AlertTitle>
      <Box
        sx={{
          color: '#1565c0',
          fontSize: '0.875rem',
          lineHeight: 1.4,
        }}
        dangerouslySetInnerHTML={{
          __html: getWarningText().replace(
            /\*\*(.*?)\*\*/g,
            '<strong>$1</strong>',
          ),
        }}
      />
    </Alert>
  );
};

export default PaymentActiveBoletoDelinquencyWarning;
